/**
 * Basic Information Form Component
 */

import React, { useState } from 'react';
import { FormData, StateInfo } from '../types';

interface BasicInfoFormProps {
  formData: FormData;
  onChange: (field: keyof FormData, value: any) => void;
  onNext: () => void;
  states: StateInfo[];
}

const BasicInfoForm: React.FC<BasicInfoFormProps> = ({ formData, onChange, onNext, states }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.marriageDate) {
      newErrors.marriageDate = 'Please select your marriage date';
    }

    if (!formData.state) {
      newErrors.state = 'Please select the state where you are filing for divorce';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Basic Information
      </h2>

      <div className="space-y-6">
        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => onChange('email', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="your.email@example.com"
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email}</p>
          )}
          <p className="text-gray-500 text-sm mt-1">
            Your report will be sent to this email address
          </p>
        </div>

        {/* First Name (Optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            First Name (Optional)
          </label>
          <input
            type="text"
            value={formData.firstName || ''}
            onChange={(e) => onChange('firstName', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="John"
          />
        </div>

        {/* Last Name (Optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Last Name (Optional)
          </label>
          <input
            type="text"
            value={formData.lastName || ''}
            onChange={(e) => onChange('lastName', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Doe"
          />
        </div>

        {/* Marriage Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Marriage Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.marriageDate}
            onChange={(e) => onChange('marriageDate', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.marriageDate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.marriageDate && (
            <p className="text-red-500 text-sm mt-1">{errors.marriageDate}</p>
          )}
        </div>

        {/* Separation Date (Optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Separation Date (Optional)
          </label>
          <input
            type="date"
            value={formData.separationDate || ''}
            onChange={(e) => onChange('separationDate', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-gray-500 text-sm mt-1">
            Leave blank if not yet separated
          </p>
        </div>

        {/* State Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            State <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.state}
            onChange={(e) => onChange('state', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.state ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select a state</option>
            {Array.isArray(states) && states.length > 0 ? (
              states.map((state) => (
                <option key={state.name} value={state.name}>
                  {state.name} - {state.type}
                </option>
              ))
            ) : (
              <option disabled>Loading states...</option>
            )}
          </select>
          {errors.state && (
            <p className="text-red-500 text-sm mt-1">{errors.state}</p>
          )}
          <p className="text-gray-500 text-sm mt-1">
            Select the state where you are filing for divorce
          </p>
        </div>
      </div>

      {/* Next Button */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleNext}
          className="bg-blue-600 text-white font-semibold py-3 px-8 rounded-lg hover:bg-blue-700 transition duration-300"
        >
          Next: Assets
        </button>
      </div>
    </div>
  );
};

export default BasicInfoForm;